import { useCallback, useEffect, useRef, useState } from 'react'
import {
  callChatToChatMessage,
  listCallChatMessages,
  sendCallChatMessage,
  subscribeToCallChat,
} from '../../../services/call-chat-service'
import { mergeChatMessages } from '../../../utils/merge-messages'
import { formatUnknownError } from '../../../utils/format-error'
import type { ChatMessage } from '../../../types/chat'

export type UseInCallChatResult = {
  enabled: boolean
  messages: ChatMessage[]
  loading: boolean
  error: string | null
  sending: boolean
  send: (text: string) => Promise<void>
}

export type InCallChatBackfillGate = {
  /** This overlay is the one presenting the call — all three call overlays stay mounted. */
  presenting: boolean
  /** The local user is in the call (phase active); before that the server refuses the read. */
  joined: boolean
  isGroup: boolean
  /** The chat panel is open. A 1:1 call only backfills once it is. */
  panelOpen: boolean
}

/** One retry: by the time we fetch, the participant row already exists. */
const BACKFILL_RETRY_DELAYS_MS = [2_000]

/** Pure: whether the history backfill may run now. */
export function shouldBackfillInCallChat(gate: InCallChatBackfillGate): boolean {
  return gate.presenting && gate.joined && (gate.isGroup || gate.panelOpen)
}

/**
 * In-call chat — CALL-SCOPED ONLY. Messages typed during the call are:
 *  1. Stored against the call (CALL#{callId}/CHAT#…) via sendCallChatMessage, so
 *     they appear later in the call summary (CallArtifacts.chat) alongside the
 *     transcript. They are deliberately NOT posted into the originating DM/group
 *     thread — in-call chat lives with the call, not the conversation.
 *  2. Fanned out live to every in-call participant via the onCallChatMessage
 *     (callId) subscription (call-chat-service), the only delivery path that
 *     reaches a peer who is in the call overlay. This also covers calls with NO
 *     conversationId (roster / urgent leadership rings).
 *
 * History sent before the panel was opened is backfilled once via
 * listCallChatMessages; new messages stream in over the subscription (no
 * polling, so no refetch storms). The backfill waits for `gate`: only the
 * presenting overlay, only once the local user has joined, and for a 1:1 call
 * only once the panel is open — fetching at call start from all three mounted
 * overlays, before the participant row existed, logged three "Not authorized
 * to view this call" errors on every call (2026-10-19).
 *
 * The `conversationId` argument is retained for call-site compatibility but is
 * intentionally unused now that in-call chat is call-scoped only.
 */
export function useInCallChat(
  callId: string | null | undefined,
  currentUserId: string | undefined,
  _conversationId: string | null | undefined,
  gate: InCallChatBackfillGate,
): UseInCallChatResult {
  const enabled = Boolean(callId && currentUserId)
  const canBackfill = enabled && shouldBackfillInCallChat(gate)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const currentUserIdRef = useRef(currentUserId)
  currentUserIdRef.current = currentUserId
  /** The callId whose history is already merged in. */
  const backfilledCallIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!callId || !currentUserId) {
      setMessages([])
      setLoading(false)
      backfilledCallIdRef.current = null
      return
    }
    const unsubscribe = subscribeToCallChat(callId, (msg) => {
      setMessages((prev) =>
        mergeChatMessages(prev, [callChatToChatMessage(msg, currentUserIdRef.current)]),
      )
    })
    return unsubscribe
  }, [callId, currentUserId])

  useEffect(() => {
    if (!canBackfill || !callId || backfilledCallIdRef.current === callId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void (async () => {
      for (let attempt = 0; !cancelled; attempt++) {
        try {
          const items = await listCallChatMessages(callId)
          if (cancelled) return
          backfilledCallIdRef.current = callId
          setMessages((prev) =>
            mergeChatMessages(
              prev,
              items.map((m) => callChatToChatMessage(m, currentUserIdRef.current)),
            ),
          )
          setError(null)
          setLoading(false)
          return
        } catch (e) {
          const delay = BACKFILL_RETRY_DELAYS_MS[attempt]
          if (delay == null) {
            if (!cancelled) {
              setError(formatUnknownError(e))
              setLoading(false)
            }
            return
          }
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }
    })()
    return () => {
      cancelled = true
      setLoading(false)
    }
  }, [callId, currentUserId, canBackfill])

  const send = useCallback(
    async (rawText: string) => {
      const text = rawText.trim()
      if (!callId || !currentUserId || !text || sending) return
      const optimisticId = `pending-${Date.now()}`
      const optimistic: ChatMessage = {
        id: optimisticId,
        threadId: callId,
        text,
        sender: 'me',
        senderId: currentUserId,
        createdAt: Date.now(),
        messageType: 'TEXT',
      }
      setMessages((prev) => [...prev, optimistic])
      setSending(true)

      try {
        // Live delivery to in-call participants + call-scoped record (so the
        // message appears in the call summary). This is what the peer sees in
        // the call overlay in realtime. In-call chat is NOT written to the
        // originating DM/group thread.
        const sent = await sendCallChatMessage(callId, text)
        setMessages((prev) =>
          mergeChatMessages(
            prev.filter((m) => m.id !== optimisticId),
            [callChatToChatMessage(sent, currentUserIdRef.current)],
          ),
        )
        setError(null)
      } catch (e) {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
        setError(formatUnknownError(e))
      } finally {
        setSending(false)
      }
    },
    [callId, currentUserId, sending],
  )

  return { enabled, messages, loading, error, sending, send }
}
